/**
 * Library View Module
 * Handles rendering and management of the library view including:
 * - Clip grid display
 * - Filtering by difficulty
 * - Search functionality
 * - Series view
 */

import appState from '../../core/app-state.js';
import { logEvent } from '../../core/event-logger.js';
import { EVENT_TYPES } from '../../core/event-types.js';
import { i18nService } from '../../services/i18n-service.js';
import { isClipCompleted } from '../../services/clip-coordinator.js';
import type { Clip } from '../../types/clip.js';

/**
 * Callback functions type
 */
interface LibraryCallbacks {
  resetClipState: () => void;
  renderClip: (clip: Clip) => void;
  switchMode: (mode: 'home' | 'library' | 'exercise') => void;
  savePreferences: () => void;
}

// Import functions that will be called from library view
// These will need to be imported from main.js or moved to appropriate modules
let resetClipState: (() => void) | undefined;
let renderClip: ((clip: Clip) => void) | undefined;
let switchMode: ((mode: 'home' | 'library' | 'exercise') => void) | undefined;
let savePreferences: (() => void) | undefined;

/**
 * Register callbacks for functions defined in main.js
 * This allows the library module to call main.js functions without circular dependencies
 */
export function registerLibraryCallbacks(callbacks: LibraryCallbacks): void {
  resetClipState = callbacks.resetClipState;
  renderClip = callbacks.renderClip;
  switchMode = callbacks.switchMode;
  savePreferences = callbacks.savePreferences;
}

/**
 * Create a clip ID info icon with tooltip for display
 * @param clipId - The clip ID to display
 * @returns A span element with info icon, tooltip, and accessibility attributes
 */
function createClipIdBadge(clipId: string): HTMLSpanElement {
  // Create container span
  const infoIcon = document.createElement('span');
  infoIcon.className = 'clip-id-info';
  infoIcon.setAttribute('role', 'button');
  infoIcon.setAttribute('aria-label', `Show clip ID: ${clipId}`);
  infoIcon.setAttribute('tabindex', '0');
  infoIcon.textContent = 'ⓘ';
  
  // Create tooltip
  const tooltip = document.createElement('span');
  tooltip.className = 'clip-id-tooltip';
  tooltip.textContent = clipId;
  tooltip.setAttribute('role', 'tooltip');
  infoIcon.appendChild(tooltip);
  
  // Handle click/tap for touch devices
  let tooltipTimeout: ReturnType<typeof setTimeout> | null = null;
  
  const toggleTooltip = (e: Event) => {
    e.stopPropagation();
    
    // Toggle tooltip visibility
    const isShown = infoIcon.classList.contains('show-tooltip');
    
    if (isShown) {
      infoIcon.classList.remove('show-tooltip');
      if (tooltipTimeout) {
        clearTimeout(tooltipTimeout);
        tooltipTimeout = null;
      }
    } else {
      infoIcon.classList.add('show-tooltip');
      
      // Auto-hide after 3 seconds on touch devices
      tooltipTimeout = setTimeout(() => {
        infoIcon.classList.remove('show-tooltip');
        tooltipTimeout = null;
      }, 3000);
    }
  };
  
  // Add event listeners for touch/click
  infoIcon.addEventListener('click', toggleTooltip);
  infoIcon.addEventListener('touchstart', (e) => {
    e.preventDefault();
    toggleTooltip(e);
  });
  
  // Keyboard support
  infoIcon.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      toggleTooltip(e);
    }
  });
  
  return infoIcon;
}

/**
 * Get list of available difficulty levels from clips
 */
function getAvailableDifficultyLevels(): string[] {
  const levels = new Set<string>();
  appState.clips.forEach(clip => {
    if (clip.difficulty) {
      levels.add(clip.difficulty.toLowerCase());
    }
  });
  return Array.from(levels).sort();
}

/**
 * Format a slug string to a title case string
 */
function formatSlugToTitle(slug: string | number | null | undefined): string {
  if (!slug) return '';
  
  // Convert to string if it's a number
  const slugStr = String(slug);
  
  // Split by hyphen, capitalize each word
  const words = slugStr.split('-').map(word => {
    return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
  });
  
  // Join with space
  return words.join(' ');
}

/**
 * Derive the status of a clip (completed, in-progress, not-started)
 */
function deriveClipStatus(clipId: string): 'completed' | 'in-progress' | 'not-started' {
  const clip = appState.clips.find(c => c.id === clipId);
  if (!clip) return 'not-started';
  
  // If marked as completed in progressMap
  if (isClipCompleted(clipId)) {
    return 'completed';
  }
  
  // Zero-question clips are treated as completed automatically
  if (!clip.questions || clip.questions.length === 0) {
    return 'completed';
  }
  
  // Check if it's the current clip and has any answered questions
  if (clip.id === appState.clips[appState.currentClipIdx]?.id && appState.currentQuestionIndex > 0) {
    return 'in-progress';
  }
  
  return 'not-started';
}

/**
 * Convert status code to user-friendly text
 */
function getStatusText(status: 'completed' | 'in-progress' | 'not-started'): string {
  switch (status) {
    case 'completed':
      return i18nService.getString('statusCompleted') || 'Completed';
    case 'in-progress':
      return i18nService.getString('statusInProgress') || 'In Progress';
    case 'not-started':
    default:
      return i18nService.getString('statusNotStarted') || 'Not Started';
  }
}

/**
 * Get series name from clip's storyName field or fallback to formatting storyId
 */
function getSeriesName(storyIdOrClip: Clip | string | number | null): string {
  // If a clip object is passed, check for storyName field first
  if (typeof storyIdOrClip === 'object' && storyIdOrClip !== null) {
    if ('storyName' in storyIdOrClip && storyIdOrClip.storyName) {
      return storyIdOrClip.storyName as string;
    }
    // Fallback to storyId if no storyName
    if (storyIdOrClip.storyId) {
      return formatSlugToTitle(storyIdOrClip.storyId);
    }
  }
  
  // If just a string storyId is passed, format it
  if (typeof storyIdOrClip === 'string' || typeof storyIdOrClip === 'number') {
    return formatSlugToTitle(storyIdOrClip);
  }
  
  return '';
}

/**
 * Get filtered clips based on current filter state
 */
function getFilteredClips(): Clip[] {
  let filtered = [...appState.clips];
  
  // Filter by learning language
  const learningLang = appState.preferences['learningLanguage'] as string | undefined;
  if (learningLang && learningLang !== 'all') {
    filtered = filtered.filter(clip => {
      return clip.language === learningLang;
    });
  }
  
  if (appState.libraryFilters.difficulty) {
    filtered = filtered.filter(clip => 
      clip.difficulty && clip.difficulty.toLowerCase() === appState.libraryFilters.difficulty
    );
  }
  
  if (appState.libraryFilters.search) {
    const searchLower = appState.libraryFilters.search.toLowerCase();
    filtered = filtered.filter(clip => 
      clip.title && clip.title.toLowerCase().includes(searchLower)
    );
  }
  
  return filtered;
}

/**
 * Handle filter level selection
 */
function selectFilterLevel(level: string, segmentedContainer: HTMLElement): void {
  // Update visual state
  const chips = Array.from(segmentedContainer.children);
  chips.forEach(chip => {
    chip.classList.remove('segmented__chip--active');
    chip.setAttribute('aria-checked', 'false');
    chip.setAttribute('tabindex', '-1');
  });
  
  const selectedChip = chips.find(chip => 
    chip.textContent.toLowerCase() === (level === 'all' ? 'all' : level.toUpperCase())
  );
  
  if (selectedChip) {
    selectedChip.classList.add('segmented__chip--active');
    selectedChip.setAttribute('aria-checked', 'true');
    selectedChip.setAttribute('tabindex', '0');
    if ('focus' in selectedChip && typeof selectedChip.focus === 'function') {
      selectedChip.focus();
    }
  }
  
  // Update application state
  const prevDifficulty = appState.libraryFilters.difficulty;
  appState.libraryFilters.difficulty = level === 'all' ? null : level;
  
  // Save to preferences
  (appState.preferences as { filterLevel?: string }).filterLevel = level;
  if (savePreferences) {
    savePreferences();
  }
  
  // Log event only if difficulty actually changed
  if (prevDifficulty !== appState.libraryFilters.difficulty) {
    const eventType = EVENT_TYPES['LIBRARY_FILTER_APPLIED'];
    if (eventType) {
      logEvent(eventType, {
        difficulty: level === 'all' ? null : level
      });
    }
  }
  
  // Re-render library
  renderLibraryView();
}

/**
 * Render filter controls (difficulty and search)
 */
function renderFilterControls(container: HTMLElement): void {
  const filterContainer = document.createElement('div');
  filterContainer.style.cssText = 'margin-bottom: 1rem; padding: 0.75rem; background-color: #f9fafb; border-radius: 0.5rem; display: flex; gap: 0.75rem; flex-wrap: wrap; align-items: center;';
  
  // Difficulty filter label
  const filterLabel = document.createElement('span');
  filterLabel.textContent = i18nService.getString('difficulty') || 'Difficulty:';
  filterLabel.style.cssText = 'font-size: 0.875rem; font-weight: 600; color: #374151;';
  filterContainer.appendChild(filterLabel);
  
  // Get available difficulty levels dynamically
  const availableLevels = getAvailableDifficultyLevels();
  const filterLevels = ['all', ...availableLevels];
  
  // Create segmented control container
  const segmentedContainer = document.createElement('div');
  segmentedContainer.className = 'segmented';
  segmentedContainer.setAttribute('role', 'radiogroup');
  segmentedContainer.setAttribute('aria-label', 'Filter clips by difficulty level');
  
  // Current selection - use preference or default to 'all'
  const currentFilterLevel = appState.libraryFilters.difficulty || 'all';
  
  filterLevels.forEach((level) => {
    const chip = document.createElement('button');
    chip.className = 'segmented__chip';
    if (level === currentFilterLevel) {
      chip.classList.add('segmented__chip--active');
      chip.setAttribute('aria-checked', 'true');
    } else {
      chip.setAttribute('aria-checked', 'false');
    }
    
    chip.setAttribute('role', 'radio');
    chip.setAttribute('tabindex', level === currentFilterLevel ? '0' : '-1');
    
    // Format label
    const label = level === 'all' ? 'All' : level.toUpperCase();
    chip.textContent = label;
    chip.setAttribute('aria-label', `Filter by ${label === 'All' ? 'all levels' : `${label} level`}`);
    
    // Click handler
    chip.addEventListener('click', () => {
      selectFilterLevel(level, segmentedContainer);
    });
    
    // Keyboard navigation
    chip.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
        e.preventDefault();
        const chips = Array.from(segmentedContainer.children);
        const currentIndex = chips.indexOf(chip);
        let nextIndex;
        
        if (e.key === 'ArrowLeft') {
          nextIndex = currentIndex === 0 ? chips.length - 1 : currentIndex - 1;
        } else {
          nextIndex = currentIndex === chips.length - 1 ? 0 : currentIndex + 1;
        }
        
        const nextChip = chips[nextIndex];
        if (nextChip && 'focus' in nextChip && typeof nextChip.focus === 'function') {
          nextChip.focus();
        }
      } else if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        selectFilterLevel(level, segmentedContainer);
      }
    });
    
    segmentedContainer.appendChild(chip);
  });
  
  filterContainer.appendChild(segmentedContainer);
  container.appendChild(filterContainer);
  
  // Add search input
  const searchContainer = document.createElement('div');
  searchContainer.style.cssText = 'margin-bottom: 1rem; padding: 0.75rem; background-color: #f9fafb; border-radius: 0.5rem;';
  
  const searchLabel = document.createElement('label');
  searchLabel.textContent = i18nService.getString('search') || 'Search:';
  searchLabel.style.cssText = 'font-size: 0.875rem; font-weight: 600; color: #374151; display: block; margin-bottom: 0.5rem;';
  searchLabel.setAttribute('for', 'library-search-input');
  searchContainer.appendChild(searchLabel);
  
  const searchInput = document.createElement('input');
  searchInput.id = 'library-search-input';
  searchInput.type = 'text';
  searchInput.placeholder = i18nService.getString('searchPlaceholder') || 'Search by clip title...';
  searchInput.value = appState.libraryFilters.search || '';
  searchInput.style.cssText = 'width: 100%; padding: 0.5rem; border: 1px solid #d1d5db; border-radius: 0.375rem; font-size: 0.875rem;';
  searchInput.setAttribute('aria-label', i18nService.getString('searchPlaceholder') || 'Search clips by title');
  
  let searchTimeout: ReturnType<typeof setTimeout> | undefined;
  searchInput.addEventListener('input', (e) => {
    const currentValue = (e.target as HTMLInputElement).value;
    const cursorPosition = (e.target as HTMLInputElement).selectionStart;
    
    // Update the filter state immediately (no trim for live updates)
    appState.libraryFilters.search = currentValue;
    
    clearTimeout(searchTimeout);
    searchTimeout = setTimeout(() => {
      const query = currentValue.trim();
      const prevSearch = appState.libraryFilters.search.trim();
      
      // Only log if the search actually changed
      if (query !== prevSearch && (query || prevSearch)) {
        const eventType = EVENT_TYPES['LIBRARY_SEARCH_APPLIED'];
        if (eventType) {
          logEvent(eventType, {
            query: query
          });
        }
      }
      
      // Store the current focus state before re-rendering
      const searchInputWasFocused = document.activeElement === e.target;
      
      renderLibraryView();
      
      // Restore focus and cursor position after re-render
      if (searchInputWasFocused) {
        const newSearchInput = document.getElementById('library-search-input') as HTMLInputElement | null;
        if (newSearchInput && cursorPosition !== null) {
          newSearchInput.focus();
          newSearchInput.setSelectionRange(cursorPosition, cursorPosition);
        }
      }
    }, 250);
  });
  
  searchContainer.appendChild(searchInput);
  container.appendChild(searchContainer);
}

/**
 * Render series detail view
 */
function renderSeriesView(container: HTMLElement): void {
  const seriesClips = appState.clips.filter(c => c.storyId === appState.seriesView);
  
  if (seriesClips.length === 0) {
    appState.seriesView = null;
    renderLibraryView();
    return;
  }
  
  // Sort by storyPart
  seriesClips.sort((a, b) => (a.storyPart || 0) - (b.storyPart || 0));
  
  // Breadcrumb navigation
  const breadcrumb = document.createElement('div');
  breadcrumb.style.cssText = 'margin-bottom: 1rem; display: flex; align-items: center; gap: 0.5rem; font-size: 0.875rem;';
  
  const backButton = document.createElement('button');
  backButton.innerHTML = '← Back to Library';
  backButton.style.cssText = 'color: #3b82f6; font-weight: 600; background: none; border: none; cursor: pointer; padding: 0;';
  backButton.setAttribute('aria-label', 'Return to library view');
  
  backButton.addEventListener('click', () => {
    appState.seriesView = null;
    renderLibraryView();
  });
  
  breadcrumb.appendChild(backButton);
  container.appendChild(breadcrumb);
  
  // Series title
  const firstClip = seriesClips[0];
  const seriesTitle = document.createElement('h1');
  seriesTitle.textContent = `Series: ${firstClip ? getSeriesName(firstClip) : 'Unknown'}`;
  seriesTitle.style.cssText = 'font-size: 1.5rem; font-weight: 700; margin-bottom: 0.75rem; color: #1f2937;';
  seriesTitle.tabIndex = -1;
  container.appendChild(seriesTitle);
  
  // Series description
  const seriesDescription = document.createElement('p');
  seriesDescription.textContent = `${seriesClips.length} clips in this series`;
  seriesDescription.style.cssText = 'margin-bottom: 1rem; color: #6b7280; font-size: 0.875rem;';
  container.appendChild(seriesDescription);
  
  // Continue Series button (if there's an incomplete clip)
  const nextIncomplete = seriesClips.find(c => !isClipCompleted(c.id));
  if (nextIncomplete) {
    const continueButton = document.createElement('button');
    continueButton.textContent = 'Continue Series';
    continueButton.style.cssText = 'padding: 0.625rem 1.25rem; background-color: #3b82f6; color: white; font-weight: 600; border-radius: 0.375rem; border: none; cursor: pointer; margin-bottom: 1.5rem;';
    continueButton.setAttribute('aria-label', `Continue series with ${nextIncomplete.title}`);
    
    continueButton.addEventListener('click', () => {
      const clipIndex = appState.clips.findIndex(c => c.id === nextIncomplete.id);
      if (clipIndex >= 0) {
        if (resetClipState) resetClipState();
        appState.currentClipIdx = clipIndex;
        const currentClip = appState.clips[clipIndex];
        if (renderClip && currentClip) renderClip(currentClip);
        if (switchMode) switchMode('exercise');
      }
    });
    
    container.appendChild(continueButton);
  }
  
  // Series clips list
  const seriesList = document.createElement('div');
  seriesList.className = 'library-list';
  seriesList.setAttribute('role', 'list');
  
  seriesClips.forEach((clip) => {
    const clipStatus = deriveClipStatus(clip.id);
    const index = appState.clips.findIndex(c => c.id === clip.id);
    
    const clipButton = document.createElement('button');
    clipButton.className = 'library-item';
    clipButton.setAttribute('role', 'listitem');
    
    // Create title and metadata container
    const titleContainer = document.createElement('div');
    titleContainer.style.cssText = 'display: flex; flex-direction: column; align-items: flex-start; flex: 1;';
    
    const titleEl = document.createElement('div');
    titleEl.className = 'library-item-title';
    
    // Add title text with part number
    const titleText = document.createElement('span');
    titleText.textContent = `Part ${clip.storyPart}: ${clip.title}`;
    titleEl.appendChild(titleText);
    
    // Add clip ID badge (subtle grey for quality control)
    titleEl.appendChild(createClipIdBadge(clip.id));
    
    titleContainer.appendChild(titleEl);
    
    // Metadata
    const metadataContainer = document.createElement('div');
    metadataContainer.style.cssText = 'display: flex; gap: 0.5rem; margin-top: 0.25rem;';
    
    if (clip.difficulty) {
      const diffBadge = document.createElement('span');
      diffBadge.textContent = clip.difficulty.toUpperCase();
      diffBadge.style.cssText = 'font-size: 0.75rem; padding: 0.125rem 0.375rem; background-color: #f3f4f6; color: #1f2937; border-radius: 0.25rem; font-weight: 600; border: 1px solid #d1d5db;';
      metadataContainer.appendChild(diffBadge);
    }
    
    if (metadataContainer.children.length > 0) {
      titleContainer.appendChild(metadataContainer);
    }
    
    // Status badge
    const statusBadge = document.createElement('span');
    statusBadge.className = `library-status-badge ${clipStatus}`;
    statusBadge.textContent = getStatusText(clipStatus);
    
    clipButton.appendChild(titleContainer);
    clipButton.appendChild(statusBadge);
    
    clipButton.addEventListener('click', () => {
      if (resetClipState) resetClipState();
      appState.currentClipIdx = index;
      const currentClip = appState.clips[index];
      if (renderClip && currentClip) renderClip(currentClip);
      if (switchMode) switchMode('exercise');
    });
    
    seriesList.appendChild(clipButton);
  });
  
  container.appendChild(seriesList);
}

/**
 * Main library view rendering function
 * Renders the library grid with clips, filters, and search
 */
export function renderLibraryView(): void {
  const libraryContainer = document.getElementById('library-container');
  if (!libraryContainer) return;
  
  // Clear existing content
  libraryContainer.innerHTML = '';
  
  // Check if we're in series view mode
  if (appState.seriesView) {
    renderSeriesView(libraryContainer);
    return;
  }
  
  // Create heading for library
  const heading = document.createElement('h1');
  heading.id = 'library-heading';
  heading.className = 'library-heading text-left';
  heading.textContent = i18nService.getString('library') || 'Library';
  heading.tabIndex = -1; // Make it focusable for accessibility
  libraryContainer.appendChild(heading);
  
  // Handle empty library case
  if (!appState.clips || appState.clips.length === 0) {
    const emptyMessage = document.createElement('p');
    emptyMessage.className = 'library-empty-message';
    emptyMessage.textContent = 'No clips available.';
    libraryContainer.appendChild(emptyMessage);
    return;
  }
  
  // Add filter controls
  renderFilterControls(libraryContainer);
  
  // Apply filters to get filtered clips
  const filteredClips = getFilteredClips();
  
  // Separate clips into completed and not-completed, storing status to avoid recalculation
  const notCompletedClips: Array<{ clip: Clip; status: 'completed' | 'in-progress' | 'not-started' }> = [];
  const completedClips: Array<{ clip: Clip; status: 'completed' | 'in-progress' | 'not-started' }> = [];
  
  filteredClips.forEach((clip) => {
    const clipStatus = deriveClipStatus(clip.id);
    if (clipStatus === 'completed') {
      completedClips.push({ clip, status: clipStatus });
    } else {
      notCompletedClips.push({ clip, status: clipStatus });
    }
  });
  
  // Helper function to create a clip button element
  const createClipButton = (clip: Clip, clipStatus: 'completed' | 'in-progress' | 'not-started') => {
    const index = appState.clips.findIndex(c => c.id === clip.id);
    
    const clipButton = document.createElement('button');
    clipButton.className = 'library-item';
    clipButton.setAttribute('role', 'listitem');
    const completedLabel = clipStatus === 'completed' ? `${i18nService.getString('completedBadge') || 'Completed'}. ` : '';
    clipButton.setAttribute('aria-label', `${completedLabel}${clip.title}`);
    
    const isActive = index === appState.currentClipIdx;
    
    // Add active accent class if this is the active clip
    if (isActive) {
      clipButton.classList.add('card--active');
    }
    
    // Leading status icon: ✓ (completed) or • (not started)
    const statusIcon = document.createElement('span');
    statusIcon.className = 'list-item-leading-icon';
    if (clipStatus === 'completed') {
      statusIcon.textContent = '✓';
      statusIcon.style.cssText = 'color: #10b981; font-size: 1.125rem; font-weight: 700; min-width: 1.5rem; text-align: center;';
      statusIcon.setAttribute('aria-label', i18nService.getString('completedBadge') || 'Completed');
    } else {
      statusIcon.textContent = '•';
      statusIcon.style.cssText = 'color: #9ca3af; font-size: 1.125rem; min-width: 1.5rem; text-align: center;';
      statusIcon.setAttribute('aria-label', i18nService.getString('notStartedBadge') || 'Not started');
    }
    clipButton.appendChild(statusIcon);
    
    // Create title and metadata container
    const titleContainer = document.createElement('div');
    titleContainer.style.cssText = 'display: flex; flex-direction: column; align-items: flex-start; flex: 1; min-width: 0;';
    
    // Add series label above title if series/story metadata exists
    if (clip.storyId) {
      const seriesLabel = document.createElement('div');
      seriesLabel.className = 'card__series-label';
      seriesLabel.textContent = getSeriesName(clip);
      titleContainer.appendChild(seriesLabel);
    }
    
    // Create title element with clip ID
    const titleEl = document.createElement('div');
    titleEl.className = 'library-item-title';
    
    // Add title text
    const titleText = document.createElement('span');
    titleText.textContent = clip.title;
    titleEl.appendChild(titleText);
    
    // Add clip ID badge (subtle grey for quality control)
    titleEl.appendChild(createClipIdBadge(clip.id));
    
    titleContainer.appendChild(titleEl);
    
    // Add metadata badges (duration and difficulty, series progress moved to label)
    const metadataContainer = document.createElement('div');
    metadataContainer.style.cssText = 'display: flex; gap: 0.5rem; margin-top: 0.25rem; flex-wrap: wrap;';
    
    // Duration badge (support both 'duration' and 'lengthSec' for backward compatibility)
    const durationInSeconds = clip.duration;
    if (durationInSeconds) {
      const durationBadge = document.createElement('span');
      durationBadge.className = 'clip-badge duration-badge';
      const minutes = Math.floor(durationInSeconds / 60);
      const seconds = durationInSeconds % 60;
      durationBadge.textContent = `${minutes}:${seconds.toString().padStart(2, '0')}`;
      durationBadge.style.cssText = 'font-size: 0.75rem; padding: 0.125rem 0.375rem; background-color: #f9fafb; color: #6b7280; border-radius: 0.25rem; font-weight: 500;';
      durationBadge.setAttribute('aria-label', `Duration: ${minutes} minutes ${seconds} seconds`);
      metadataContainer.appendChild(durationBadge);
    }
    
    // Difficulty badge
    if (clip.difficulty) {
      const difficultyBadge = document.createElement('span');
      difficultyBadge.className = 'clip-badge difficulty-badge';
      difficultyBadge.textContent = clip.difficulty.toUpperCase();
      difficultyBadge.style.cssText = 'font-size: 0.75rem; padding: 0.125rem 0.375rem; background-color: #f3f4f6; color: #1f2937; border-radius: 0.25rem; font-weight: 600; border: 1px solid #d1d5db;';
      difficultyBadge.setAttribute('aria-label', `Difficulty: ${clip.difficulty}`);
      metadataContainer.appendChild(difficultyBadge);
    }
    
    if (metadataContainer.children.length > 0) {
      titleContainer.appendChild(metadataContainer);
    }
    
    // Add chevron icon (decorative, right-aligned with fixed width)
    const chevron = document.createElement('span');
    chevron.className = 'card__chevron';
    chevron.setAttribute('aria-hidden', 'true');
    chevron.innerHTML = '›';
    chevron.style.cssText = 'min-width: 1.5rem; text-align: center;';
    
    // Add to button
    clipButton.appendChild(titleContainer);
    clipButton.appendChild(chevron);
    
    // Add click event
    clipButton.addEventListener('click', () => {
      // Reset state and load the selected clip
      if (resetClipState) resetClipState();
      appState.currentClipIdx = index;
      const currentClip = appState.clips[index];
      if (renderClip && currentClip) renderClip(currentClip);
      if (switchMode) switchMode('exercise');
    });
    
    return clipButton;
  };
  
  // Create library list for not-completed clips
  const libraryList = document.createElement('div');
  libraryList.className = 'library-list';
  libraryList.setAttribute('role', 'list');
  
  // Add not-completed clips
  notCompletedClips.forEach(({ clip, status }) => {
    libraryList.appendChild(createClipButton(clip, status));
  });
  
  libraryContainer.appendChild(libraryList);
  
  // Add collapsible section for completed clips if there are any
  if (completedClips.length > 0) {
    const completedSection = document.createElement('div');
    completedSection.className = 'library-completed-section';
    
    // Collapsible header button
    const headerButton = document.createElement('button');
    headerButton.className = 'library-completed-header';
    headerButton.setAttribute('aria-expanded', 'false');
    headerButton.setAttribute('aria-label', `Completed clips (${completedClips.length}). Click to expand or collapse.`);
    
    const headerText = document.createElement('span');
    headerText.textContent = `Completed (${completedClips.length})`;
    
    const chevronIcon = document.createElement('span');
    chevronIcon.className = 'library-completed-chevron';
    chevronIcon.setAttribute('aria-hidden', 'true');
    chevronIcon.textContent = '▼';
    
    headerButton.appendChild(headerText);
    headerButton.appendChild(chevronIcon);
    
    // Collapsible content
    const completedList = document.createElement('div');
    completedList.className = 'library-list library-completed-list library-completed-list--hidden';
    completedList.setAttribute('role', 'list');
    
    // Add completed clips to the list
    completedClips.forEach(({ clip, status }) => {
      completedList.appendChild(createClipButton(clip, status));
    });
    
    // Toggle functionality
    headerButton.addEventListener('click', () => {
      const isExpanded = headerButton.getAttribute('aria-expanded') === 'true';
      
      if (isExpanded) {
        // Collapse
        headerButton.setAttribute('aria-expanded', 'false');
        completedList.classList.add('library-completed-list--hidden');
        chevronIcon.classList.remove('library-completed-chevron--expanded');
      } else {
        // Expand
        headerButton.setAttribute('aria-expanded', 'true');
        completedList.classList.remove('library-completed-list--hidden');
        chevronIcon.classList.add('library-completed-chevron--expanded');
      }
    });
    
    completedSection.appendChild(headerButton);
    completedSection.appendChild(completedList);
    libraryContainer.appendChild(completedSection);
  }
}

/**
 * Show series view for a specific storyId
 */
export function showSeriesView(storyId: number): void {
  appState.seriesView = storyId;
  
  const eventType = EVENT_TYPES['SERIES_VIEW_OPENED'];
  if (eventType) {
    logEvent(eventType, {
      seriesId: storyId
    });
  }
  
  renderLibraryView();
}

// Export the library view module
export const libraryView = {
  render: renderLibraryView,
  showSeries: showSeriesView,
  registerCallbacks: registerLibraryCallbacks
};

export default libraryView;
